// AttendeeProfileModal.jsx
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ThumbsUp, Flag, ShieldAlert } from "lucide-react";
import "./AttendeeProfileModal.css";

const STATIC_PROFILES = {
  "user-1": {
    name: "Alex Chen",
    title: "Partner",
    company: "TechCorp Ventures",
    country: "United States",
    avatar: "",
    bio: "Early-stage investor focused on AI and enterprise infrastructure. Previously built and sold two SaaS companies.",
    seeking: "Late-seed AI startups with strong technical founders.",
    giving: "Intros to West Coast VCs and enterprise GTM advisors.",
    interests: ["AI", "SaaS", "Enterprise"],
    is_locked: false,
  },
  "user-2": {
    name: "Jordan Martinez",
    title: "Founder & CEO",
    company: "Greentide",
    country: "United Kingdom",
    avatar: "",
    bio: "Building climate finance infrastructure at scale. $2B+ deployed across 40+ countries.",
    seeking: "Institutional LPs interested in climate and energy transition.",
    giving:
      "Deep expertise in carbon markets and renewable energy project finance.",
    interests: ["Climate", "Capital", "Fintech"],
    is_locked: false,
  },
  "user-3": {
    name: "Sam Patel",
    title: "Head of Platform",
    company: "Venture Fund",
    country: "India",
    avatar: "",
    bio: "Connecting founders with the resources they need to scale. Operator turned investor.",
    seeking: "Founders building in Web3 and fintech infrastructure.",
    giving: "Network across Southeast Asian and Indian startup ecosystems.",
    interests: ["Web3", "Fintech", "Founders"],
    is_locked: false,
  },
  "user-4": {
    name: "Taylor Reed",
    title: "CTO",
    company: "Paused Corp",
    country: "Canada",
    avatar: "",
    bio: "Engineering leader with 15 years in distributed systems. Open source contributor.",
    seeking: "Technical co-founders and early-stage engineering hires.",
    giving: "Architecture reviews and scaling advice for early-stage teams.",
    interests: ["Research", "Scale", "AI"],
    is_locked: true,
  },
};

export default function AttendeeProfileModal({
  userId,
  attendeeId,
  eventId,
  open,
  onClose,
}) {
  const id = userId || attendeeId || null;
  const visible = open === undefined ? !!id : open;

  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [liked, setLiked] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [flagCategory, setFlagCategory] = useState("general");

  useEffect(() => {
    if (!visible || !id) return;
    setProfile(null);
    setLiked(false);
    setFlagOpen(false);
    setFlagReason("");

    const timer = setTimeout(() => {
      const found = STATIC_PROFILES[id];
      setProfile(found || { error: true });
    }, 400);

    return () => clearTimeout(timer);
  }, [visible, id]);

  if (!visible || !id) return null;

  const like = async () => {
    setBusy(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    try {
      setLiked(true);
      toast.success("Like sent.");
    } catch {
      toast.error("Couldn't send like.");
    } finally {
      setBusy(false);
    }
  };

  const submitFlag = async () => {
    if (!flagReason.trim()) return toast.error("Add a short reason.");
    setBusy(true);
    await new Promise((resolve) => setTimeout(resolve, 600));
    try {
      toast.success("Thanks — our AI moderator will review and notify a host.");
      setFlagOpen(false);
      setFlagReason("");
      onClose?.();
    } catch {
      toast.error("Couldn't submit flag.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modalOverlay"
      data-testid="attendee-profile-modal"
      onClick={onClose}
    >
      <div className="modalCard glass" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="attendee-profile-close"
          className="modalCloseBtn"
        >
          <X size={18} />
        </button>

        {!profile && <p className="modalLoadingText">Loading…</p>}
        {profile && profile.error && (
          <p className="modalErrorText">Couldn't load profile.</p>
        )}

        {profile && !profile.error && (
          <div data-testid="attendee-profile-body">
            {/* Header */}
            <div className="profileHeader">
              <div className="profileAvatar">
                {profile.avatar ? (
                  <img
                    src={profile.avatar}
                    alt={profile.name}
                    className="profileAvatarImg"
                  />
                ) : (
                  <span className="profileAvatarInitial">
                    {(profile.name || "?").slice(0, 1)}
                  </span>
                )}
              </div>
              <div className="profileMeta">
                <h3 className="profileName" data-testid="attendee-profile-name">
                  {profile.name}
                </h3>
                {(profile.title || profile.company) && (
                  <p className="profileTitleCompany">
                    {[profile.title, profile.company]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                )}
                {profile.country && (
                  <p className="profileCountry">{profile.country}</p>
                )}
              </div>
            </div>

            {/* Locked banner */}
            {profile.is_locked && (
              <div
                className="profileLockedBanner"
                data-testid="attendee-profile-locked"
              >
                <ShieldAlert size={13} /> This member is currently held by a
                host.
              </div>
            )}

            {/* Bio */}
            {profile.bio && (
              <div className="profileSection">
                <p className="overline profileSectionLabel">Bio</p>
                <p className="profileSectionText">{profile.bio}</p>
              </div>
            )}

            {/* Seeking */}
            {profile.seeking && (
              <div className="profileSection">
                <p className="overline profileSectionLabel">Seeking</p>
                <p className="profileSectionText">{profile.seeking}</p>
              </div>
            )}

            {/* Giving */}
            {profile.giving && (
              <div className="profileSection">
                <p className="overline profileSectionLabel">Giving</p>
                <p className="profileSectionText">{profile.giving}</p>
              </div>
            )}

            {/* Interests */}
            {(profile.interests || []).length > 0 && (
              <div className="profileInterests">
                {profile.interests.map((interest) => (
                  <span key={interest} className="profileInterestTag">
                    {interest}
                  </span>
                ))}
              </div>
            )}

            {/* Action bar */}
            {!flagOpen && (
              <div className="profileActions">
                <button
                  type="button"
                  onClick={like}
                  disabled={busy || liked}
                  data-testid="attendee-profile-like"
                  className={`profileLikeBtn ${liked ? "profileLikeBtnLiked" : "profileLikeBtnDefault"}`}
                >
                  <ThumbsUp size={12} /> {liked ? "Liked" : "Like contribution"}
                </button>
                <button
                  type="button"
                  onClick={() => setFlagOpen(true)}
                  data-testid="attendee-profile-flag"
                  className="profileFlagBtn"
                >
                  <Flag size={12} /> Flag
                </button>
              </div>
            )}

            {/* Flag form */}
            {flagOpen && (
              <div className="flagForm" data-testid="flag-form">
                <p className="flagFormTitle">
                  <Flag size={11} /> Flag this member
                </p>
                <p className="flagFormDesc">
                  Our AI moderator classifies flags and may auto-lock
                  high-severity cases. A host reviews every flag.
                </p>
                <div className="flagCategories">
                  {["general", "harassment", "spam", "doxxing"].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setFlagCategory(c)}
                      data-testid={`flag-category-${c}`}
                      className={`flagCategoryBtn ${
                        flagCategory === c
                          ? "flagCategoryBtnActive"
                          : "flagCategoryBtnIdle"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <textarea
                  rows={3}
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  data-testid="flag-reason"
                  placeholder="A short, factual description — what happened, where, when."
                  className="flagTextarea"
                />
                <div className="flagFormFooter">
                  <button
                    onClick={() => setFlagOpen(false)}
                    className="flagCancelBtn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitFlag}
                    disabled={busy}
                    data-testid="flag-submit"
                    className="flagSubmitBtn"
                  >
                    Submit flag
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
